import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { randomUUID } from "node:crypto";

import {
  CliError,
  EXIT_INPUT,
  EXIT_NEEDS_AGENT,
  EXIT_OK,
  EXIT_RUNTIME,
  type CommandOutput,
  directDir,
  exists,
  readJson,
  readText,
  scriptJsonPath,
  sha256Text,
  writeJson,
} from "../../common.js";
import { atomicWriteJson } from "../../infra/script-working-copy.js";
import { applyPatchOperations } from "../../domain/script/patch/apply.js";
import { blankScriptV4 } from "../../domain/script/v4/factory.js";
import { collectAssetRefs, collectStateRefs } from "../../domain/script/refs.js";
import {
  expandSelectorToActionRefs,
  isDangerousRegex,
  MAX_GREP_PATTERN_LEN,
  parseInSelector,
  selectorSingleActionPoint,
  type InSelector,
} from "../../domain/script/selector.js";
import {
  parseAnyAddress,
  parseActionRef,
  parseAssetTarget,
  parseSceneRef,
  parseStateTarget,
} from "../../domain/script/address.js";
import { PATCH_OP_SCHEMA } from "../../domain/script/schema.js";
import { LocalScriptOutputStore } from "../../infra/local-script-output-store.js";
import {
  ScriptOutputApiError,
  resolveOutputMode,
  type ConfigMissingSlot,
  type ScriptOutputStore,
} from "../../infra/script-output-store.js";
import { summarizeIssues } from "../../domain/script/text-util.js";
import { sortDeep } from "../../domain/script/text-util.js";
import {
  asList,
  type Dict,
  isDict,
  isList,
  strOf,
} from "../../domain/script/shared.js";
import {
  ScriptEditSession, scriptOutputClient, apiErrorToCli, currentRevisionOrZero,
  loadScriptForEdit, validateSession, saveScriptSession, syncValidationResult, assertTargetCoherent,
  scriptSourceText, patchOperationsFromPayload, parseBound, episodeCharCounts, requestIdForScriptWrite,
} from "./session.js";
import {
  tokenizeLine, DO_BOOL_FLAGS, DO_REPEAT_FLAGS, ParsedDoLine, parseDoLine, doErr, lineToOp, actionAddress, formatInspectAction, SKIPPABLE_EDIT_CODES, applySingleScriptOp, summarizeScriptOp, regexPattern, applySelectorEdits, actorIdsInAction, buildGrepMatcher, buildReport, synopsisPreview, episodeInSelector, nameMaps, formatScene, ActionTarget, HAS_FILTERS, resolveActionTargets, selectionHasAnyFilter, listAssetsByKind, splitSpeakerList, blankCreateReport, requireNameArg,
} from "./lib.js";

export async function commandCreate(opts: Dict): Promise<[CommandOutput, number]> {
  const workspace = strOf(opts["workspace_path"] || "workspace");
  const force = Boolean(opts["force"]);
  const title = strOf(opts["title"]).trim();
  const script = blankScriptV4(title);

  const localPath = scriptJsonPath(opts);
  if (exists(localPath) && !force) {
    throw new CliError("A script already exists at the target.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_EXISTS",
            hint: "Edit the existing script with the atomic verbs, or pass --force to overwrite with a blank scaffold.",
            data: { expected: ["a non-existent target, or --force to overwrite"], actual: [localPath] },
          });
  }
  atomicWriteJson(localPath, script);
  const session = new ScriptEditSession({ workspace, scriptPath: localPath, script });
  const validation = validateSession(session);
  const passed = Boolean(validation["passed"]);
  return [blankCreateReport(session, validation, null), passed ? EXIT_OK : EXIT_NEEDS_AGENT];
}
